// The lab-test vocabulary and the order-status rule — everything about lab
// orders that both the backend and the frontend need and neither should own.
//
// Inert like the other cross-stack packages: no drizzle, no Zod, no React, no
// I/O. It is a preset list, the specimen/container vocabulary, and pure
// predicates over an order's parts. Anything that throws the project's errors,
// touches a table, or needs a translator belongs on one side or the other.
//
// The presets live in code rather than being seeded into each clinic for two
// reasons. They mirror `INVESTIGATION_PRESETS` + `suggestion_catalogue`, which
// already work this way. And seeding them would mean a migration inserting rows
// that use enum values the same migration created — which Postgres forbids
// inside one transaction, and drizzle's migrator wraps every pending file in
// one. A clinic's own tests live in `lab_test_catalogue` and are merged over
// these by `code` (see the lab service).

export const SPECIMEN_TYPES = ["blood", "urine", "stool", "other"] as const;
export type SpecimenType = (typeof SPECIMEN_TYPES)[number];

const SPECIMEN_TYPE_SET: ReadonlySet<string> = new Set(SPECIMEN_TYPES);

export function isSpecimenType(value: string): value is SpecimenType {
  return SPECIMEN_TYPE_SET.has(value);
}

/**
 * A test definition, whether it came from the presets below or a clinic's own
 * `lab_test_catalogue` row. `container` is the tube or pot the sample goes in —
 * a hint the collection screen shows so nobody has to remember which is which.
 */
export interface LabTestDefinition {
  code: string;
  name: string;
  specimenType: SpecimenType;
  container?: string;
}

/**
 * The starting catalogue. Codes are stable identifiers and MUST NOT be renamed
 * once shipped — `lab_order_tests.test_code` snapshots them, and the partial
 * unique index that stops a test being ordered twice on one requisition is
 * written over that column. Display names may change freely; the snapshot on
 * each ordered test keeps old requisitions readable.
 */
export const LAB_TEST_PRESETS: readonly LabTestDefinition[] = [
  // --- Blood -------------------------------------------------------------
  { code: "FBC", name: "Full blood count (FBC/CBC)", specimenType: "blood", container: "EDTA" },
  { code: "ESR", name: "Erythrocyte sedimentation rate", specimenType: "blood", container: "EDTA" },
  { code: "CRP", name: "C-reactive protein", specimenType: "blood", container: "Plain" },
  { code: "FBS", name: "Fasting blood sugar", specimenType: "blood", container: "Fluoride" },
  { code: "RBS", name: "Random blood sugar", specimenType: "blood", container: "Fluoride" },
  { code: "HBA1C", name: "HbA1c", specimenType: "blood", container: "EDTA" },
  { code: "LIPID", name: "Lipid profile", specimenType: "blood", container: "Plain" },
  { code: "CREAT", name: "Serum creatinine", specimenType: "blood", container: "Plain" },
  { code: "UREA", name: "Blood urea", specimenType: "blood", container: "Plain" },
  { code: "ELECTRO", name: "Serum electrolytes (Na/K)", specimenType: "blood", container: "Plain" },
  { code: "LFT", name: "Liver function tests", specimenType: "blood", container: "Plain" },
  { code: "BILI", name: "Serum bilirubin", specimenType: "blood", container: "Plain" },
  { code: "TSH", name: "TSH", specimenType: "blood", container: "Plain" },
  { code: "FT4", name: "Free T4", specimenType: "blood", container: "Plain" },
  { code: "BLOODGRP", name: "Blood group & Rh", specimenType: "blood", container: "EDTA" },
  { code: "PTINR", name: "Prothrombin time / INR", specimenType: "blood", container: "Citrate" },
  { code: "BLOODPIC", name: "Blood picture", specimenType: "blood", container: "EDTA" },
  { code: "MP", name: "Malaria parasite", specimenType: "blood", container: "EDTA" },
  { code: "DENGUE", name: "Dengue NS1 / IgM", specimenType: "blood", container: "Plain" },
  { code: "CALCIUM", name: "Serum calcium", specimenType: "blood", container: "Plain" },
  { code: "URICACID", name: "Serum uric acid", specimenType: "blood", container: "Plain" },

  // --- Urine -------------------------------------------------------------
  { code: "UFR", name: "Urine full report", specimenType: "urine", container: "Sterile container" },
  { code: "UCULT", name: "Urine culture & ABST", specimenType: "urine", container: "Sterile container" },
  { code: "UPROT24", name: "Urine protein (24 hour)", specimenType: "urine", container: "24h collection bottle" },
  { code: "UPT", name: "Urine pregnancy test", specimenType: "urine", container: "Sterile container" },
  { code: "UMALB", name: "Urine microalbumin", specimenType: "urine", container: "Sterile container" },

  // --- Stool -------------------------------------------------------------
  { code: "SFR", name: "Stool full report", specimenType: "stool", container: "Stool container" },
  { code: "SOB", name: "Stool occult blood", specimenType: "stool", container: "Stool container" },
  { code: "SCULT", name: "Stool culture", specimenType: "stool", container: "Stool container" },
];

// ---------------------------------------------------------------------------
// Order limits
// ---------------------------------------------------------------------------
//
// These live here rather than in the API's Zod schemas because BOTH sides
// enforce them and they must agree. The new-order form stops you selecting a
// 41st test and refuses a collection time in the future; the server refuses the
// same things at the boundary. Two copies of the number would eventually differ,
// and the failure mode is a form that lets you fill it in and a request that
// then rejects it.

/** Ceiling on tests per requisition. A runaway guard, not a clinical policy. */
export const MAX_TESTS_PER_ORDER = 40;

/**
 * How far into the future a collection time may sit.
 *
 * Not zero: a client clock a few seconds ahead of the server would otherwise
 * reject an honest "collected just now". Not open-ended: a future draw time is
 * either a wrong device clock or a typo, and both sort the worklist wrong.
 */
export const MAX_COLLECTION_SKEW_MS = 5 * 60 * 1000;

/** Prefix marking a test that exists in no catalogue — see `freeTextCode`. */
export const FREE_TEXT_CODE_PREFIX = "free:";

/**
 * Build the `test_code` for a test the clinic typed rather than picked.
 *
 * The prefix is what keeps the `(order_id, test_code)` unique index meaningful:
 * without it a typo could collide with a real catalogue code and be rejected as
 * a duplicate of a test that was never ordered.
 */
export function freeTextCode(name: string): string {
  const slug = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return `${FREE_TEXT_CODE_PREFIX}${slug}`;
}

export function isFreeTextCode(code: string): boolean {
  return code.startsWith(FREE_TEXT_CODE_PREFIX);
}

/**
 * The specimens a set of tests requires — one per distinct specimen type, in
 * the canonical `SPECIMEN_TYPES` order so the collection screen doesn't reorder
 * itself as tests are toggled.
 *
 * The container is taken from the first test of that type that names one. Two
 * blood tests wanting different tubes is real (EDTA vs fluoride), and this
 * deliberately does NOT model it: the collection screen shows the container as
 * an editable hint, and the one-live-specimen-per-type index is what the whole
 * flow is built on. Splitting by container would mean two blood specimens per
 * order and a different index; that is a schema change, not a helper change.
 */
export function deriveRequiredSpecimens(
  tests: readonly Pick<LabTestDefinition, "specimenType" | "container">[],
): { specimenType: SpecimenType; container: string | undefined }[] {
  return SPECIMEN_TYPES.flatMap((specimenType) => {
    const forType = tests.filter((t) => t.specimenType === specimenType);
    if (forType.length === 0) return [];
    return [{ specimenType, container: forType.find((t) => t.container)?.container }];
  });
}

// ---------------------------------------------------------------------------
// Derived order status
// ---------------------------------------------------------------------------

export const LAB_ORDER_STATUSES = [
  "cancelled",
  "awaiting_collection",
  "recollect_required",
  "resulted",
  "partially_resulted",
  "collected",
] as const;
export type LabOrderStatus = (typeof LAB_ORDER_STATUSES)[number];

export type LabTestStatus = "pending" | "resulted" | "cancelled";

/** The parts of an order `deriveOrderStatus` reads. Structural only — no PHI. */
export interface OrderStatusInput {
  cancelledAt: Date | string | null;
  specimens: readonly {
    id: string;
    specimenType: SpecimenType;
    rejectedAt: Date | string | null;
  }[];
  tests: readonly {
    specimenId: string | null;
    specimenType: SpecimenType;
    status: LabTestStatus;
  }[];
}

/**
 * An order has no stored status column — this is the single definition of what
 * one is, read by the API projection and rendered by the client.
 *
 * The evaluation ORDER below is part of the contract, and it closes two traps:
 *
 *  - `resulted` requires at least one non-cancelled test. Without that clause,
 *    cancelling the last pending test on an otherwise-resulted order makes it
 *    read as complete, and an order whose every test was cancelled reads as
 *    complete having produced nothing.
 *  - `awaiting_collection` is checked before `recollect_required`, so an order
 *    with one uncollected test and one rejected specimen reports the thing that
 *    still needs doing first rather than the reason it needs doing again.
 */
export function deriveOrderStatus(input: OrderStatusInput): LabOrderStatus {
  if (input.cancelledAt !== null) return "cancelled";

  const live = input.tests.filter((t) => t.status !== "cancelled");
  if (live.length === 0) return "collected";

  const rejectedIds = new Set(
    input.specimens.filter((s) => s.rejectedAt !== null).map((s) => s.id),
  );

  // A test whose specimen was rejected is reset to (specimenId: null, pending)
  // in the same transaction as the rejection, so it lands here via the null
  // check. The `rejectedIds` arm covers a row that somehow kept its pointer.
  //
  // Only a PENDING test can be outstanding. A resulted one keeps its pointer at
  // the rejected specimen forever — `reopenTestsForSpecimen` deliberately only
  // reopens pending rows, because a report that already exists is not undone by
  // the tube later being called haemolysed. Without the status clause that
  // permanent pointer reads as collection work, and an order whose every test
  // is resulted sits at the top of the worklist as `awaiting_collection` for
  // good, invisible to `?status=resulted`.
  const uncollected = live.filter(
    (t) =>
      t.status === "pending" &&
      (t.specimenId === null || rejectedIds.has(t.specimenId)),
  );

  if (uncollected.length > 0) {
    // Both of these are "someone has to collect something", and the difference
    // is only about WHY — so it has to be decided per specimen type, not per
    // order. An order whose blood was rejected but whose urine was never taken
    // has ordinary collection work outstanding; calling that `recollect_required`
    // would tell the worklist a redraw is all that is left, and the urine would
    // sit there unmentioned.
    //
    // So: a redraw is required only when EVERY outstanding test is outstanding
    // because its own specimen type was rejected and not replaced.
    const liveTypes = new Set(
      input.specimens.filter((s) => s.rejectedAt === null).map((s) => s.specimenType),
    );
    const rejectedTypes = new Set(
      input.specimens.filter((s) => s.rejectedAt !== null).map((s) => s.specimenType),
    );

    const everyOutstandingIsARedraw = uncollected.every(
      (t) => rejectedTypes.has(t.specimenType) && !liveTypes.has(t.specimenType),
    );

    return everyOutstandingIsARedraw ? "recollect_required" : "awaiting_collection";
  }

  const resulted = live.filter((t) => t.status === "resulted").length;
  if (resulted === live.length) return "resulted";
  if (resulted > 0) return "partially_resulted";
  return "collected";
}
